import React, { useEffect, useState, useCallback } from "react";
import styled from "styled-components";
import { useAuth0 } from "@auth0/auth0-react";
import axiosInstance from "../../../../../appHelpers/axiosInstance";
import { GlassCard, Badge, Loader, NoDataState } from "../../../../ui";

const STATUS_PILL = {
  ok:    { variant: "success", label: "OK" },
  stale: { variant: "warning", label: "STALE" },
  error: { variant: "error",   label: "ERROR" },
  cold:  { variant: "neutral", label: "COLD" },
};

const HISTORY_LIMIT = 50;
const BAR_COUNT = 48;

const formatRelative = (iso) => {
  if (!iso) return "never";
  const t = new Date(iso).getTime();
  const diff = (Date.now() - t) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
};

export const EdgeSystemTabFC = ({ sme }) => {
  const { getAccessTokenSilently } = useAuth0();
  const [latest, setLatest] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const token = await getAccessTokenSilently({
        audience: `${process.env.REACT_APP_AUTH0_EXPRESS_API}`,
      });
      const [latestRes, historyRes] = await Promise.all([
        axiosInstance({ url: `/edge/system/${sme}`, headers: { authorization: `Bearer ${token}` } }),
        axiosInstance({ url: `/edge/system/${sme}/history?limit=${HISTORY_LIMIT}`, headers: { authorization: `Bearer ${token}` } }),
      ]);
      setLatest(latestRes?.data || null);
      setHistory(historyRes?.data || []);
      setError(null);
    } catch (e) {
      console.error("edge system fetch failed", e);
      setError("Failed to load edge data for this system.");
    } finally {
      setLoading(false);
    }
  }, [getAccessTokenSilently, sme]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  if (loading && !latest) {
    return <Center><Loader size="medium" message="Loading edge data…" /></Center>;
  }

  if (error) {
    return <NoDataState title="Error" description={error} showRetry onRetry={fetchAll} />;
  }

  if (!latest) {
    return <NoDataState title="No edge record" description="This SME has heartbeats but no fleet_boxes entry." />;
  }

  const pill = STATUS_PILL[latest.status] || { variant: "neutral", label: "?" };
  const heartbeatsReceived = latest.heartbeats_received_24h ?? 0;
  const clockSkew = latest.box_clock_delta_seconds;
  const failed = latest.failed_collectors || [];

  return (
    <Wrap>
      <TopRow>
        <GlassCard>
          <Inner>
            <Title>Current Status</Title>
            <StatusLine>
              <Badge variant={pill.variant}>{pill.label}</Badge>
              <span>last heartbeat <strong>{formatRelative(latest.last_heartbeat)}</strong>
                {latest.rows_inserted != null ? ` · ${latest.rows_inserted} rows inserted` : ""}</span>
            </StatusLine>
            <Meta>
              <MetaItem><K>App version</K><V>{latest.app_version || "—"}</V></MetaItem>
              <MetaItem><K>Magnet type</K><V>{latest.magnet_type || "—"}</V></MetaItem>
              <MetaItem><K>Network</K><V>{latest.network_type || "—"}</V></MetaItem>
              <MetaItem><K>Hostname</K><V>{latest.hostname || "—"}</V></MetaItem>
              <MetaItem><K>MAC</K><V>{latest.mac || "—"}</V></MetaItem>
              <MetaItem><K>SSH IP</K><V>{latest.ssh_ip || "—"}</V></MetaItem>
            </Meta>
            {Math.abs(clockSkew || 0) > 60 ? (
              <Warn>Box clock skew: {clockSkew}s (threshold ±60s)</Warn>
            ) : null}
            {failed.length > 0 ? (
              <Chips>
                <ChipsLabel>Failed collectors:</ChipsLabel>
                {failed.map((c) => <Chip key={c}>{c}</Chip>)}
              </Chips>
            ) : null}
          </Inner>
        </GlassCard>

        <GlassCard>
          <Inner>
            <Title>Last 24h</Title>
            <Big>{heartbeatsReceived} / {BAR_COUNT}</Big>
            <Hint>heartbeats received</Hint>
            <BarRow>
              {Array.from({ length: BAR_COUNT }).map((_, i) => (
                <Bar key={i} on={i < heartbeatsReceived} />
              ))}
            </BarRow>
            <Legend>
              <span><Sw style={{ background: "#10b981" }} />received</span>
              <span><Sw style={{ background: "#e5e7eb" }} />missed</span>
            </Legend>
          </Inner>
        </GlassCard>
      </TopRow>

      <GlassCard>
        <Inner>
          <Title>Recent heartbeats</Title>
          <Table>
            <thead>
              <tr>
                <Th>Timestamp</Th>
                <Th>Status</Th>
                <Th>App ver</Th>
                <Th>Rows</Th>
                <Th>Notes</Th>
              </tr>
            </thead>
            <tbody>
              {history.map((h) => {
                const p = STATUS_PILL[h.status] || { variant: "neutral", label: (h.status || "?").toUpperCase() };
                return (
                  <tr key={h.id}>
                    <Td mono>{new Date(h.timestamp).toISOString().replace("T", " ").slice(0, 19)}</Td>
                    <Td><Badge variant={p.variant}>{p.label}</Badge></Td>
                    <Td>{h.app_version || "—"}</Td>
                    <Td>{h.rows_inserted ?? "—"}</Td>
                    <Td>{h.error_message || "—"}</Td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        </Inner>
      </GlassCard>
    </Wrap>
  );
};

const Wrap   = styled.div``;
const Center = styled.div`display:flex; justify-content:center; align-items:center; min-height:200px;`;
const TopRow = styled.div`display:grid; grid-template-columns:2fr 1fr; gap:0.75rem; margin-bottom:1rem;
  @media (max-width: 768px) { grid-template-columns: 1fr; }`;
const Inner  = styled.div`padding:1rem 1.25rem;`;
const Title  = styled.div`font-size:0.7rem; font-weight:600; color:rgba(0,0,0,0.55);
  text-transform:uppercase; letter-spacing:0.5px; margin-bottom:0.5rem;`;
const StatusLine = styled.div`display:flex; align-items:center; gap:0.8rem; font-size:0.9rem;`;
const Meta = styled.div`display:grid; grid-template-columns:repeat(3,1fr); gap:0.6rem; margin-top:0.8rem;
  @media (max-width: 768px) { grid-template-columns: 1fr; }`;
const MetaItem = styled.div``;
const K = styled.div`font-size:0.7rem; color:rgba(0,0,0,0.5);`;
const V = styled.div`font-size:0.85rem; font-family:ui-monospace,monospace; font-weight:500;`;
const Warn = styled.div`margin-top:0.6rem; padding:0.4rem 0.6rem; background:#fef3c7; color:#92400e;
  border-radius:6px; font-size:0.8rem;`;
const Chips = styled.div`margin-top:0.6rem; display:flex; flex-wrap:wrap; gap:0.3rem; align-items:center;`;
const ChipsLabel = styled.span`font-size:0.75rem; color:rgba(0,0,0,0.55); margin-right:0.3rem;`;
const Chip = styled.span`background:#fee2e2; color:#991b1b; font-size:0.7rem; padding:0.1rem 0.5rem;
  border-radius:8px; font-family:ui-monospace,monospace;`;
const Big  = styled.div`font-size:1.4rem; font-weight:700; color:#10b981;`;
const Hint = styled.div`font-size:0.72rem; color:rgba(0,0,0,0.55);`;
const BarRow = styled.div`display:flex; gap:2px; align-items:end; height:34px; margin-top:0.5rem;`;
const Bar = styled.div`width:6px; height:30px; border-radius:1px;
  background: ${({ on }) => (on ? "#10b981" : "#e5e7eb")};`;
const Legend = styled.div`display:flex; gap:0.8rem; font-size:0.7rem; color:rgba(0,0,0,0.55); margin-top:0.5rem;`;
const Sw = styled.span`display:inline-block; width:10px; height:10px; vertical-align:middle;
  margin-right:0.25rem; border-radius:2px;`;
const Table = styled.table`width:100%; border-collapse:collapse; font-size:0.8rem; margin-top:0.3rem;`;
const Th = styled.th`text-align:left; padding:0.45rem 0.6rem; font-size:0.68rem; font-weight:600;
  text-transform:uppercase; color:rgba(0,0,0,0.5); border-bottom:1px solid rgba(0,0,0,0.08);`;
const Td = styled.td`padding:0.45rem 0.6rem; border-bottom:1px solid rgba(0,0,0,0.04);
  font-family: ${({ mono }) => (mono ? "ui-monospace, monospace" : "inherit")};`;
